import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sessionsAPI, Session } from '../api/client';
import { AdminLayout } from '../components/Layout';
import './Dashboard.css';

/**
 * Icon Button with Tooltip
 */
const IconButton = ({
  icon,
  tooltip,
  onClick,
  className = '',
  ariaLabel,
}: {
  icon: React.ReactNode;
  tooltip: string;
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
  ariaLabel?: string;
}) => (
  <button
    className={`icon-btn ${className}`}
    onClick={onClick}
    aria-label={ariaLabel || tooltip}
  >
    {icon}
    <span className="tooltip">{tooltip}</span>
  </button>
);

/**
 * Icon Link with Tooltip
 */
const IconLink = ({
  to,
  icon,
  tooltip,
  onClick,
  className = '',
  ariaLabel,
}: {
  to: string;
  icon: React.ReactNode;
  tooltip: string;
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
  ariaLabel?: string;
}) => (
  <Link
    to={to}
    className={`icon-btn ${className}`}
    onClick={onClick}
    aria-label={ariaLabel || tooltip}
  >
    {icon}
    <span className="tooltip">{tooltip}</span>
  </Link>
);

/**
 * Status Badge Component
 */
const StatusBadge = ({ status }: { status: Session['status'] }) => {
  const statusConfig: Record<Session['status'], { label: string; className: string; icon?: React.ReactNode }> = {
    'pending': { label: 'Pending', className: 'status-pending' },
    'in-progress': { label: 'In Progress', className: 'status-progress' },
    'approved': {
      label: 'Complete',
      className: 'status-approved status-complete',
      icon: (
        <svg viewBox="0 0 20 20" fill="currentColor" className="status-icon">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
    },
    'rejected': { label: 'Rejected', className: 'status-rejected' },
    'mixed': { label: 'Mixed', className: 'status-mixed' },
  };

  const config = statusConfig[status] || statusConfig['pending'];

  return (
    <span className={`status-badge ${config.className}`}>
      {config.icon}
      {config.label}
    </span>
  );
};

/**
 * Format date for display
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format relative time
 */
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
};

/**
 * Dashboard Page
 *
 * Displays a list of all proof sessions with their status.
 * Allows creating new sessions and viewing existing ones.
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopyLink = useCallback(async (e: React.MouseEvent, proofLink: string, sessionId: string) => {
    e.stopPropagation();
    try {
      // Try modern Clipboard API first (requires secure context)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(proofLink);
      } else {
        // Fallback for non-secure contexts (HTTP)
        const textArea = document.createElement('textarea');
        textArea.value = proofLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedId(sessionId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert(`Copy this link manually:\n${proofLink}`);
    }
  }, []);

  const fetchSessions = async (pageNum: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await sessionsAPI.list(pageNum, 10);
      setSessions(response.sessions);
      setTotalPages(response.totalPages);
      setTotal(response.total);
      setPage(response.page);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setError('Failed to load sessions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions(1);
  }, []);

  const handleRowClick = (sessionId: string) => {
    navigate(`/admin/sessions/${sessionId}`);
  };

  return (
    <AdminLayout>
      <div className="dashboard">
        {/* Page Header */}
        <div className="dashboard-header">
          <div>
            <h1>Proof Sessions</h1>
            <p className="dashboard-subtitle">
              {total > 0 ? `${total} total sessions` : 'Manage your proof review sessions'}
            </p>
          </div>
          <Link to="/admin/sessions/new" className="btn-create">
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
            </svg>
            New Session
          </Link>
        </div>

        {/* Error State */}
        {error && (
          <div className="dashboard-error" role="alert">
            <p>{error}</p>
            <button onClick={() => fetchSessions(page)}>Retry</button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="dashboard-loading">
            <div className="dashboard-spinner" />
            <p>Loading sessions...</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && sessions.length === 0 && (
          <div className="dashboard-empty">
            <div className="empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
            </div>
            <h2>No proof sessions yet</h2>
            <p>Create your first proof session to get started.</p>
            <Link to="/admin/sessions/new" className="btn-create-large">
              Create Your First Session
            </Link>
          </div>
        )}

        {/* Sessions Table */}
        {!isLoading && !error && sessions.length > 0 && (
          <>
            <div className="sessions-table-wrapper">
              <table className="sessions-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Last Activity</th>
                    <th className="th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => (
                    <tr
                      key={session.id}
                      onClick={() => handleRowClick(session.id)}
                      className="session-row"
                    >
                      <td>
                        <div className="customer-info">
                          <span className="customer-name">{session.customer_name}</span>
                          <span className="customer-email">{session.customer_email}</span>
                          <span className="session-id-badge" title={`Full ID: ${session.id}`}>ID: {session.id.slice(0, 8)}</span>
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={session.status} />
                      </td>
                      <td className="td-date">
                        <span title={formatDate(session.created_at)}>
                          {formatRelativeTime(session.created_at)}
                        </span>
                      </td>
                      <td className="td-date">
                        <span title={formatDate(session.completed_at || session.started_at || session.created_at)}>
                          {formatRelativeTime(session.completed_at || session.started_at || session.created_at)}
                        </span>
                      </td>
                      <td className="td-actions">
                        <div className="action-buttons">
                          {/* Copy Link Button */}
                          <IconButton
                            icon={
                              copiedId === session.id ? (
                                <svg viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                                </svg>
                              )
                            }
                            tooltip={copiedId === session.id ? 'Copied!' : 'Copy proof link'}
                            onClick={(e) => handleCopyLink(e, session.proof_link || '', session.id)}
                            className={copiedId === session.id ? 'icon-btn-success' : ''}
                            ariaLabel={`Copy proof link for ${session.customer_name}`}
                          />
                          {/* View Session Button */}
                          <IconLink
                            to={`/admin/sessions/${session.id}`}
                            icon={
                              <svg viewBox="0 0 20 20" fill="currentColor">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                              </svg>
                            }
                            tooltip="View session"
                            onClick={(e) => e.stopPropagation()}
                            ariaLabel={`View session for ${session.customer_name}`}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => fetchSessions(page - 1)}
                  disabled={page === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => fetchSessions(page + 1)}
                  disabled={page === totalPages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
